// w43 Qog'oz ertagi (Paper Meadow storybook) — closed-book wedding preview
// card.
//
// The full template opens on a 3D book resting on a warm desk: a cream
// cover with a gold title-plate, rotating open into a paper-meadow scene.
// The catalog card compresses that into one still frame: a warm desk-brown
// backdrop, a cream "book cover" rectangle with a rounded spine on its
// right edge (the book faces left, spine catching the desk light on the
// right), a thin gold-plate inset holding the couple's initials monogram,
// names and a date rule, and a scatter of tiny paper-petal dots drifting
// off the cover. Uses the site's already-loaded serif stack (via `font`)
// for the monogram only — no new font import.
import { type LayoutCtx } from "./_shared";

// Deterministic paper-petal dots — same positions/sizes/rotations on every
// render (no Math.random so SSR/CSR + repeated mounts stay visually
// stable). Scattered around the book so the "paper meadow" motif reads even
// as a still frame.
const PETALS: Array<{ x: number; y: number; r: number; o: number; rot: number }> = [
  { x: 8, y: 16, r: 3, o: 0.55, rot: -20 },
  { x: 14, y: 34, r: 2.4, o: 0.4, rot: 15 },
  { x: 6, y: 56, r: 2.8, o: 0.5, rot: -35 },
  { x: 12, y: 76, r: 2.2, o: 0.4, rot: 25 },
  { x: 90, y: 14, r: 2.6, o: 0.5, rot: 30 },
  { x: 94, y: 40, r: 3, o: 0.45, rot: -15 },
  { x: 88, y: 64, r: 2.2, o: 0.4, rot: 10 },
  { x: 92, y: 84, r: 2.6, o: 0.5, rot: -25 },
];

// Split "Timur & Madina" / "Jasur va Gulnora" / "Дилшод и Мадина" into
// initials joined with a middot, matching the design's gold-plate monogram.
// Falls back to a single initial when only one name is present — no
// dangling separator.
function initials(names: string): string {
  const parts = names
    .split(/\s*[&+—]\s*|\s+(?:va|и)\s+/i)
    .map((p) => p.trim())
    .filter(Boolean);
  const letters = parts.map((p) => p.charAt(0).toUpperCase());
  return letters.length > 1 ? `${letters[0]}·${letters[1]}` : letters[0] || "";
}

export function QogozPreviewLayout({ names, date, accent, ink, font }: LayoutCtx) {
  const gold = accent || "#c9a24b";
  const cream = "#f6ecd6";
  const brown = ink || "#4a3a28";
  const mono = initials(names);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        // Warm desk-brown backdrop, slightly lit from the top.
        background: `radial-gradient(120% 90% at 50% 0%, #6b543a 0%, ${brown} 55%, #362615 100%)`,
        padding: "7%",
        display: "flex",
        overflow: "hidden",
      }}
    >
      {/* Tiny paper petal dots drifting off the closed book. */}
      {PETALS.map((petal, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${petal.x}%`,
            top: `${petal.y}%`,
            width: petal.r * 2,
            height: petal.r * 2,
            borderRadius: "50% 0 50% 50%",
            background: cream,
            opacity: petal.o,
            transform: `rotate(${petal.rot}deg)`,
          }}
        />
      ))}

      {/* The book cover. */}
      <div
        style={{
          flex: 1,
          position: "relative",
          borderRadius: "6px 16px 16px 6px",
          background: cream,
          boxShadow: `0 10px 30px rgba(0,0,0,0.45), 0 2px 0 ${gold}55 inset`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "10%",
        }}
      >
        {/* Rounded spine on the right edge, catching the desk light. */}
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: "8%",
            borderRadius: "0 16px 16px 0",
            background: `linear-gradient(90deg, transparent 0%, ${gold}22 55%, ${gold}55 100%)`,
          }}
        />

        {/* Gold-plate inset. */}
        <div
          style={{
            position: "relative",
            width: "82%",
            border: `1px solid ${gold}`,
            boxShadow: `inset 0 0 0 3px ${gold}22`,
            padding: "9% 6%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 6,
              fontWeight: 700,
              letterSpacing: "0.34em",
              color: gold,
              marginBottom: "10%",
            }}
          >
            NIKOH TO'YI
          </div>

          {/* Initials monogram. */}
          <div
            style={{
              fontFamily: font,
              fontSize: 15,
              letterSpacing: "0.06em",
              color: brown,
              marginBottom: "9%",
            }}
          >
            {mono}
          </div>

          {/* Names. */}
          <div
            style={{
              fontFamily: font,
              fontSize: 15,
              lineHeight: 1.25,
              color: brown,
              marginBottom: "9%",
            }}
          >
            {names}
          </div>

          {/* Gold date rule. */}
          <div style={{ width: "50%", height: 1, background: `${gold}bb`, marginBottom: "8%" }} />

          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 6.5,
              fontWeight: 700,
              letterSpacing: "0.28em",
              color: brown,
            }}
          >
            {date.toUpperCase()}
          </div>
        </div>
      </div>
    </div>
  );
}
